import React, { useState } from 'react';
import { SearchIcon, Loader2Icon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { parseIsbnList } from './utils';
interface IsbnLookupFormProps {
  onImportComplete: () => void;
}
export const IsbnLookupForm: React.FC<IsbnLookupFormProps> = ({
  onImportComplete
}) => {
  const dk = useDarkClasses();
  const [input, setInput] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [results, setResults] = useState<{
    isbn: string;
    title?: string;
    author?: string;
    status: 'success' | 'error';
  }[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const handleSearch = () => {
    const isbns = parseIsbnList(input);
    if (isbns.length === 0) return;
    setIsFetching(true);
    setHasSearched(true);
    // Simulate API call to Open Library / Google Books
    setTimeout(() => {
      const mockResults = isbns.map((isbn, i) => {
        if (i % 4 === 3) {
          return {
            isbn,
            status: 'error' as const
          };
        }
        return {
          isbn,
          title: `Imported Book ${i + 1}`,
          author: 'Author Name',
          status: 'success' as const
        };
      });
      setResults(mockResults);
      setIsFetching(false);
    }, 1500);
  };
  const handleAddToQueue = () => {
    onImportComplete();
    setInput('');
    setResults([]);
    setHasSearched(false);
  };
  return <div className="space-y-6" data-id="element-17732" data-mp="1qj8da2912d45">
      <div data-id="element-17733" data-mp="1qk9z91rl0k0n">
        <label className={`block text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-17734" data-mp="1qk1n5c3a9iqi">
          Paste ISBNs (ISBN-10 or ISBN-13)
        </label>
        <p className={`text-xs mb-3 ${dk.textSecondary}`} data-id="element-17735" data-mp="1hhsbgiwa3jbj">
          Enter one per line or separate by commas. We'll fetch metadata from
          Open Library and Google Books.
        </p>
        <textarea value={input} onChange={e => setInput(e.target.value)} placeholder="9781234567890&#10;9780987654321" className={`w-full h-32 p-3 rounded-lg border focus:ring-2 focus:ring-purple-500 focus:border-transparent ${dk.input}`} data-id="element-17736" data-mp="3bw1lmr8az3kp" />
      </div>

      <button onClick={handleSearch} disabled={isFetching || !input.trim()} className={`flex items-center justify-center w-full sm:w-auto px-6 py-2.5 rounded-lg font-medium text-white transition-colors ${isFetching || !input.trim() ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`} data-id="element-17737" data-mp="30gq5uxyha6yr">
        {isFetching ? <>
            <Loader2Icon className="w-5 h-5 mr-2 animate-spin" data-id="element-17738" data-mp="3o8n8pzjy9onq" />
            Fetching metadata...
          </> : <>
            <SearchIcon className="w-5 h-5 mr-2" data-id="element-17739" data-mp="3eh9f28eojj2i" />
            Look up ISBNs
          </>}
      </button>

      {hasSearched && !isFetching && results.length > 0 && <div className={`mt-6 p-4 rounded-xl border ${dk.card} ${dk.border}`} data-id="element-17740" data-mp="b00z5u3iitaf">
          <h3 className={`font-bold mb-4 ${dk.textHeading}`} data-id="element-17741" data-mp="axxymg4qhhrg">
            Preview Results
          </h3>
          <div className="space-y-3 mb-6 max-h-60 overflow-y-auto pr-2" data-id="element-17742" data-mp="jarkxxx8b0zo">
            {results.map((res, idx) => <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${dk.borderSubtle} ${dk.surfaceMuted}`} data-id="element-17743" data-mp="s9cx9bc3xjs0" data-mpi={idx}>
                <div className="flex items-center gap-3" data-id="element-17744" data-mp="1279965cvcir6">
                  {res.status === 'success' ? <CheckCircleIcon className="w-5 h-5 text-green-500 shrink-0" data-id="element-17745" data-mp="168memec1ujxk" /> : <AlertCircleIcon className="w-5 h-5 text-red-500 shrink-0" data-id="element-17746" data-mp="1az2sfzjs87kg" />}
                  <div data-id="element-17747" data-mp="1fz7ahu0c9acs">
                    <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-17748" data-mp="1fxmgc5hxrtfg">
                      {res.status === 'success' ? res.title : 'Not found'}
                    </p>
                    <p className={`text-xs ${dk.textSecondary}`} data-id="element-17749" data-mp="1pwbylzdqvfw8">
                      {res.isbn} {res.author && `• ${res.author}`}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="flex justify-end" data-id="element-17750" data-mp="2hovtli76j7hr">
            <button onClick={handleAddToQueue} className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors" data-id="element-17751" data-mp="2wejtqg3mk03w">
              Add {results.filter(r => r.status === 'success').length} to
              Queue
            </button>
          </div>
        </div>}
    </div>;
};